/**
 * OptimizeMonitorContent.tsx
 * 单股自动优化二次确认对话框的内容组件
 * Claude 改 2026-05-08
 *
 * Step 1: dryRun 预览决策（含实时打分、模板变更、参数微调）
 * Step 2: 二次确认 + 实盘警告 + 实际应用
 */

import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ArrowRight, ArrowLeft, Pause, ArrowUp, ArrowDown, Settings, Loader2, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

interface Props {
  monitor: any;
  step: 0 | 1 | 2;
  preview: any;
  tradeMode: string;
  onPreviewLoaded: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function OptimizeMonitorContent({
  monitor, step, preview, tradeMode,
  onPreviewLoaded, onNext, onBack, onSuccess, onCancel,
}: Props) {
  const isLive = tradeMode.includes("实盘") || tradeMode.includes("LIVE");

  const runMutation = trpc.screening.runAutoOptimize.useMutation();

  // Step 1 加载预览
  useEffect(() => {
    if (step === 1 && monitor && !preview) {
      runMutation.mutate(
        { monitorId: monitor.id, dryRun: true, ignoreCooldown: true, forceLive: true },
        {
          onSuccess: (data: any) => onPreviewLoaded(data),
          onError: (e: any) => toast.error("预览失败: " + e.message),
        }
      );
    }
  }, [step, monitor?.id]);

  const handleApply = () => {
    if (!monitor) return;
    runMutation.mutate(
      { monitorId: monitor.id, dryRun: false, ignoreCooldown: true, forceLive: true },
      {
        onSuccess: (data: any) => {
          if (data.executed > 0) {
            toast.success(`已应用 ${data.executed} 项调整到 ${monitor.stockName}`);
            onSuccess();
          } else {
            toast.warning("无可用调整（可能没有可优化项）");
            onCancel();
          }
        },
        onError: (e: any) => toast.error("应用失败: " + e.message),
      }
    );
  };

  if (!monitor) return null;

  // Step 1: 显示预览
  if (step === 1) {
    if (!preview) {
      return (
        <div className="py-8 flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
          <p className="text-xs text-muted-foreground">正在分析实时行情、生成优化决策...</p>
        </div>
      );
    }

    const decisions = preview.decisions || [];
    const hasDecisions = decisions.length > 0;
    const decision = decisions[0]; // 单股优化，只会有一个决策

    return (
      <div className="space-y-3">
        {/* 实时评分卡 */}
        <div className="rounded-md border border-border/40 bg-muted/20 p-3">
          <div className="text-[11px] text-muted-foreground mb-1">当前监控状态</div>
          <div className="flex items-center gap-2 flex-wrap">
            {typeof monitor.t0SuitableScore === "number" && (
              <Badge variant="outline" className={`text-[10px] px-1.5 ${
                monitor.t0SuitableScore >= 70 ? "border-emerald-500/40 text-emerald-400"
                : monitor.t0SuitableScore >= 50 ? "border-muted-foreground/40 text-muted-foreground"
                : "border-red-500/40 text-red-400"
              }`}>实时T0: {monitor.t0SuitableScore}/100</Badge>
            )}
            <Badge variant="outline" className="text-[10px] px-1.5 border-blue-500/40 text-blue-400">
              当前模板: {monitor.activeTemplate || "balanced"}
            </Badge>
            {monitor.todayProfitPct != null && (
              <Badge variant="outline" className={`text-[10px] px-1.5 ${
                monitor.todayProfitPct > 0 ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"
              }`}>
                今日盈亏: {monitor.todayProfitPct > 0 ? "+" : ""}{Number(monitor.todayProfitPct).toFixed(2)}%
              </Badge>
            )}
            {(monitor.consecutiveStopLoss ?? 0) > 0 && (
              <Badge variant="outline" className="text-[10px] px-1.5 border-amber-500/40 text-amber-400">
                连续止损 {monitor.consecutiveStopLoss} 次
              </Badge>
            )}
          </div>
        </div>

        {/* 决策结果 */}
        {!hasDecisions ? (
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4 text-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
            <p className="text-sm font-medium">当前状态良好，无需调整</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              实时评分 / 表现 / 持仓状态都在合理区间，按规则不需要修改模板或参数
            </p>
          </div>
        ) : (
          <div className={`rounded-md border p-3 space-y-2 ${
            decision.shouldPause ? "border-red-500/30 bg-red-500/5"
            : decision.newTemplate === "aggressive" ? "border-orange-500/30 bg-orange-500/5"
            : decision.newTemplate === "conservative" ? "border-blue-500/30 bg-blue-500/5"
            : "border-green-500/30 bg-green-500/5"
          }`}>
            <div className="flex items-center gap-2">
              {decision.shouldPause ? <Pause className="h-4 w-4 text-red-400" />
                : decision.newTemplate === "aggressive" ? <ArrowUp className="h-4 w-4 text-orange-400" />
                : decision.newTemplate === "conservative" ? <ArrowDown className="h-4 w-4 text-blue-400" />
                : <Settings className="h-4 w-4 text-green-400" />}
              <span className="text-sm font-medium">即将执行的调整</span>
            </div>
            {decision.newTemplate && (
              <div className="text-[11px]">
                <span className="text-muted-foreground">模板切换：</span>
                <span className="font-mono">{decision.oldTemplate}</span>
                <ArrowRight className="inline h-3 w-3 mx-1" />
                <span className="font-mono font-medium">{decision.newTemplate}</span>
              </div>
            )}
            {decision.shouldPause && (
              <div className="text-[11px] text-red-400 font-medium">⚠ 该 monitor 将被暂停</div>
            )}
            {decision.paramUpdates && Object.keys(decision.paramUpdates).length > 0 && (
              <div className="text-[11px] space-y-0.5">
                <div className="text-muted-foreground">参数变更：</div>
                {Object.entries(decision.paramUpdates).map(([k, v]: [string, any]) => (
                  <div key={k} className="font-mono ml-2">
                    <span className="text-muted-foreground">{k}</span> = <span className="text-foreground">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[11px] text-muted-foreground border-t border-border/30 pt-2 mt-2">
              <span className="font-medium">原因：</span>{decision.reason}
            </div>
          </div>
        )}

        {/* 实盘警告 */}
        {isLive && hasDecisions && (
          <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
            <div className="text-[11px]">
              <span className="font-medium text-red-400">实盘模式警告：</span>
              <span className="text-muted-foreground">
                当前为「{tradeMode}」模式。模板或参数变更会影响真实交易决策，请仔细确认。
              </span>
            </div>
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/30">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-3.5 w-3.5 mr-1" />取消
          </Button>
          {hasDecisions && (
            <Button size="sm" onClick={onNext} className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border-amber-500/40">
              下一步：确认应用 <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Step 2: 二次确认
  return (
    <div className="space-y-3">
      <div className={`rounded-md border p-4 ${isLive ? "border-red-500/40 bg-red-500/10" : "border-amber-500/40 bg-amber-500/10"}`}>
        <div className="flex items-start gap-2 mb-2">
          <AlertTriangle className={`h-5 w-5 ${isLive ? "text-red-400" : "text-amber-400"} shrink-0 mt-0.5`} />
          <div>
            <p className="text-sm font-medium">最后确认</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              你即将对 <span className="font-medium text-foreground">{monitor.stockName}({monitor.stockCode})</span> 应用上一步预览的调整。
              {isLive && <span className="text-red-400 font-medium"> 当前为实盘模式，调整会立刻生效并影响真实交易。</span>}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/30">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />返回预览
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>取消</Button>
        <Button
          size="sm"
          onClick={handleApply}
          disabled={runMutation.isPending}
          className={isLive ? "bg-red-500/20 text-red-400 hover:bg-red-500/30 border-red-500/40" : "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border-amber-500/40"}
        >
          {runMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
          {isLive ? "确认实盘应用" : "确认应用"}
        </Button>
      </div>
    </div>
  );
}
